import {
  Button,
  Textarea,
  useDisclosure,
  Modal,
  ModalContent,
  ModalBody,
} from "@heroui/react";
import {
  FaPaperPlane,
  FaImage,
  FaXmark,
  FaGripVertical,
} from "react-icons/fa6";
import { useFileUploadModal } from "@/app/components/fileUpload/useFileUploadModal";
import { S3_URL } from "@/api/s3/s3";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Chat, IMessage } from "@/app/components/chat/chat";
import { useParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { Ticket, TicketType } from "@/api/tickets/tickets.ts";
import { api } from "@/api";
import { toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { EditTicketModal } from "@/app/components/modals/tickets/editTicketModal";

interface SortableAttachmentProps {
  attachment: string;
  index: number;
  onRemove: (index: number) => void;
  onPreview: (url: string) => void;
}

function SortableAttachment({
  attachment,
  index,
  onRemove,
  onPreview,
}: SortableAttachmentProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `attachment-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group cursor-grab active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <img
        src={`${S3_URL}/${attachment}`}
        alt={`Вложение ${index + 1}`}
        className="w-16 h-16 object-cover rounded border hover:opacity-80 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onPreview(`${S3_URL}/${attachment}`);
        }}
      />
      <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded flex items-center justify-center">
        <FaGripVertical className="text-white opacity-0 group-hover:opacity-100 text-xs" />
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 z-10"
      >
        ×
      </button>
    </div>
  );
}

export function TicketPage() {
  const { type, id } = useParams() as { type: string; id: string };
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<IMessage[] | null>(null);

  const [messageSending, setMessageSending] = useState<boolean>(false);
  const editTicketModalDisclosure = useDisclosure();

  const handleError = useErrorHandling();

  const [message, setMessage] = useState<string>("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileUploadModal = useFileUploadModal();

  const fetchMessages = useCallback(() => {
    api.tickets.messages
      .list(type as TicketType, Number(id))
      .then((response) => {
        setMessages(
          response.data.map((message) => {
            return {
              id: message.id,
              date: message.created_at,
              isMe: message.creator_user_type == "user",
              message: message.content,
              user: message.creator_user_name,
              attachments: message.attachments,
            };
          }),
        );
      });
  }, [id, type]);

  useEffect(() => {
    if (!type || !id) return;

    api.tickets
      .get(type as TicketType, Number(id))
      .then((response) => {
        setTicket(response.data);
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
        console.log(err);
      });

    fetchMessages();
  }, [handleError, id, type, fetchMessages, editTicketModalDisclosure.isOpen]);

  const sendMessage = useCallback(() => {
    if ((!message.trim() && attachments.length === 0) || messageSending) return;

    setMessageSending(true);
    api.tickets.messages
      .create(type as TicketType, ticket?.id || 0, {
        content: message.trim(),
        attachments: attachments,
      })
      .then(() => {
        setMessage("");
        setAttachments([]);
        fetchMessages();
      })
      .catch((err) => {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
        console.log(err);
      })
      .finally(() => {
        setMessageSending(false);
      });
  }, [
    handleError,
    message,
    attachments,
    ticket?.id,
    type,
    fetchMessages,
    messageSending,
  ]);

  const handleFileUploaded = useCallback((urls: string[]) => {
    setAttachments((prev) => [...prev, ...urls]);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setAttachments((items) => {
        const oldIndex = items.findIndex(
          (_, index) => `attachment-${index}` === active.id,
        );
        const newIndex = items.findIndex(
          (_, index) => `attachment-${index}` === over?.id,
        );

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  return (
    <>
      <EditTicketModal disclosure={editTicketModalDisclosure} ticket={ticket} />
      <div className="flex flex-col gap-2 mt-2 h-full overflow-hidden">
        <div className="flex flex-col flex-grow overflow-hidden">
          <Chat
            data={{
              id: ticket?.id || 0,
              title: ticket?.subject || "",
              adminView: false,
              messages: messages || [],
              ticket: ticket,
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          {attachments.length > 0 && (
            <div className="p-2 bg-gray-50 dark:bg-zinc-800 rounded-lg">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={attachments.map((_, index) => `attachment-${index}`)}
                  strategy={rectSortingStrategy}
                >
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((attachment, index) => (
                      <SortableAttachment
                        key={`attachment-${index}`}
                        attachment={attachment}
                        index={index}
                        onRemove={(index) =>
                          setAttachments((prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                        onPreview={setPreviewImage}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>
          )}
          <div className="flex flex-row gap-2 items-end p-2 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-700">
            <Button
              isIconOnly
              color="default"
              variant="flat"
              onPress={() => fileUploadModal.onOpen()}
              isDisabled={messageSending}
              size="sm"
              className="min-w-10 h-10"
            >
              <FaImage className="text-sm" />
            </Button>
            <div className="flex-grow">
              <Textarea
                minRows={1}
                maxRows={4}
                variant="bordered"
                placeholder="Введите сообщение..."
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                }}
                onKeyDown={handleKeyPress}
                isDisabled={messageSending}
                classNames={{
                  input: "resize-none text-sm",
                  inputWrapper:
                    "bg-transparent border-none shadow-none min-h-10",
                }}
              />
            </div>
            <Button
              isLoading={messageSending}
              isIconOnly
              color="primary"
              variant="solid"
              onPress={sendMessage}
              isDisabled={
                (!message.trim() && attachments.length === 0) || messageSending
              }
              size="sm"
              className="min-w-10 h-10"
            >
              <FaPaperPlane className="text-sm" />
            </Button>
          </div>
        </div>
        <fileUploadModal.FileUploadModal
          accept="image/*"
          multiple={true}
          onFileUploaded={handleFileUploaded}
        />

        <Modal
          isOpen={!!previewImage}
          onClose={() => setPreviewImage(null)}
          size="3xl"
          classNames={{
            backdrop: "z-[1000]",
            wrapper: "z-[1000]",
          }}
        >
          <ModalContent>
            <ModalBody className="p-0">
              <div className="relative">
                <Button
                  isIconOnly
                  color="default"
                  variant="flat"
                  className="absolute top-2 right-2 z-10 bg-black bg-opacity-50 text-white"
                  onPress={() => setPreviewImage(null)}
                >
                  <FaXmark />
                </Button>
                {previewImage && (
                  <img
                    src={previewImage}
                    alt="Просмотр изображения"
                    className="w-full h-auto max-h-[80vh] object-contain"
                  />
                )}
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      </div>
    </>
  );
}
